import { Flex, Grid, Typography, theme } from 'antd';
import { PlatformIcon } from '@/components/PlatformIcon';
import MouseFollowTooltip from '@/components/tooltips/MouseFollowTooltip';
import { useFilterContext } from '@/context';
import { useMouseFollowTooltip } from '@/hooks/useMouseFollowTooltip';
import { Platform } from '@/types';
import type { SocialProfile, SupportedSocialNetwork } from '@/types/api/social-profiles';

function getChannelLabelByType(type: SupportedSocialNetwork, count: number) {
  switch (type) {
    case Platform.Acast:
      return count > 1 ? `${count} audio podcasts` : '1 audio podcast';
    case Platform.YouTube:
      return count > 1 ? `${count} YouTube channels` : '1 YouTube channel';
    case Platform.TikTok:
      return count > 1 ? `${count} TikTok channels` : '1 TikTok channel';
    case Platform.Instagram:
      return count > 1 ? `${count} Instagram accounts` : '1 Instagram account';
    default:
      return '';
  }
}

const TooltipContent = ({
  profiles,
  type,
}: {
  profiles: SocialProfile[];
  type: SupportedSocialNetwork;
}) => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginXS}>
      <Typography.Text strong style={{ fontSize: token.fontSizeSM }}>
        {getChannelLabelByType(type, profiles.length)}
      </Typography.Text>
      <Flex vertical gap={token.marginXXS} style={{ paddingInlineStart: token.paddingMD }}>
        {profiles.map((profile) => (
          <Typography.Text
            key={`${type}-${profile.handle}`}
            style={{ fontSize: token.fontSizeSM }}
          >
            • {profile.handle}
          </Typography.Text>
        ))}
      </Flex>
    </Flex>
  );
};

export const SocialChannelProfileItemEcho = ({
  type,
  profiles,
}: {
  type: SupportedSocialNetwork;
  profiles: SocialProfile[];
}) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const profileTextMaxWidth = screens.xl ? 200 : screens.sm ? 150 : 120;
  const hasProfiles = profiles.length > 0;
  const {
    isOpen: isTooltipOpen,
    point: tooltipPoint,
    containerProps: socialRowContainerProps,
  } = useMouseFollowTooltip(hasProfiles);
  const { filters } = useFilterContext();
  const isShowFilterEnabled = filters.selectedShows.length > 0;
  if (profiles.length === 0) {
    return (
      <Flex
        align="center"
        style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
      >
        <PlatformIcon name={type} />
        <Typography.Text
          ellipsis
          style={{
            minWidth: 0,
            maxWidth: profileTextMaxWidth,
            color: token.colorTextSecondary,
            fontWeight: 300,
          }}
        >
          n/a
        </Typography.Text>
      </Flex>
    );
  }
  return (
    <Flex
      {...socialRowContainerProps}
      align="center"
      gap={token.marginXXS}
      style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
    >
      <PlatformIcon name={type} />
      <Flex align="center" flex={1} style={{ minWidth: 0, overflow: 'hidden' }}>
        {!isShowFilterEnabled && (
          <Typography.Text strong style={{ marginBottom: token.marginXS }}>
            {getChannelLabelByType(type, profiles.length)}
          </Typography.Text>
        )}
        <Typography.Text
          ellipsis
          style={{
            minWidth: 0,
            maxWidth: profileTextMaxWidth,
            color: token.colorTextSecondary,
            fontWeight: 300,
          }}
        >
          {profiles.map(
            (profile, index) => profile.handle + (index < profiles.length - 1 ? ' • ' : '')
          )}
        </Typography.Text>
      </Flex>
      <MouseFollowTooltip
        isOpen={isTooltipOpen}
        clientX={tooltipPoint.x}
        clientY={tooltipPoint.y}
        zIndex={token.zIndexPopupBase}
      >
        <TooltipContent profiles={profiles} type={type} />
      </MouseFollowTooltip>
    </Flex>
  );
};
